import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { adminCreateProperty, adminUpdateProperty } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const LANGS = ["en", "fr", "tr"];
const emptyLang = () => ({ en: "", fr: "", tr: "" });
const emptyLangList = () => ({ en: "", fr: "", tr: "" });

const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (Array.isArray(a) ? a.join("\n") : "");

const fromProperty = (p) => {
  if (!p) {
    return {
      title: emptyLang(), description: emptyLang(), features: emptyLangList(),
      listing_type: "sale", price: "", price_unit: "EUR", min_stay_months: "1", max_stay_months: "11", location: "",
      bedrooms: "", bathrooms: "", area_sqm: "", images: "", featured: false,
    };
  }
  return {
    title: { ...emptyLang(), ...(p.title || {}) },
    description: { ...emptyLang(), ...(p.description || {}) },
    features: {
      en: arrToLines(p.features?.en), fr: arrToLines(p.features?.fr), tr: arrToLines(p.features?.tr),
    },
    listing_type: p.listing_type || "sale",
    price: p.price ?? "", price_unit: p.price_unit || "EUR", location: p.location || "",
    min_stay_months: p.min_stay_months ?? "1", max_stay_months: p.max_stay_months ?? "11",
    bedrooms: p.bedrooms ?? "", bathrooms: p.bathrooms ?? "", area_sqm: p.area_sqm ?? "",
    images: arrToLines(p.images), featured: !!p.featured,
  };
};

const inputCls = "bg-background border-border/60";

export const PropertyForm = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(() => fromProperty(initial));
  const [saving, setSaving] = useState(false);
  const editing = !!initial;

  const setLang = (field, lang, v) => setF((s) => ({ ...s, [field]: { ...s[field], [lang]: v } }));
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.en.trim()) { toast.error("English title is required"); return; }
    const payload = {
      title: f.title,
      description: f.description,
      features: { en: linesToArr(f.features.en), fr: linesToArr(f.features.fr), tr: linesToArr(f.features.tr) },
      listing_type: f.listing_type,
      price: Number(f.price) || 0,
      price_unit: f.price_unit || "EUR",
      min_stay_months: f.listing_type === "mid_term" ? Number(f.min_stay_months) || 1 : null,
      max_stay_months: f.listing_type === "mid_term" ? Number(f.max_stay_months) || 11 : null,
      location: f.location,
      bedrooms: Number(f.bedrooms) || 0,
      bathrooms: Number(f.bathrooms) || 0,
      area_sqm: Number(f.area_sqm) || 0,
      images: linesToArr(f.images),
      featured: f.featured,
    };
    setSaving(true);
    try {
      if (editing) await adminUpdateProperty(initial.id, payload);
      else await adminCreateProperty(payload);
      toast.success(editing ? "Property updated" : "Property created");
      onSaved();
    } catch (err) {
      toast.error("Could not save property");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="property-form" className="flex flex-col gap-6">
      {/* Title */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {LANGS.map((l) => (
          <div key={l} className="flex flex-col gap-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Title ({l})</Label>
            <Input data-testid={`prop-title-${l}`} className={inputCls} value={f.title[l]} onChange={(e) => setLang("title", l, e.target.value)} />
          </div>
        ))}
      </div>

      {/* Description */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {LANGS.map((l) => (
          <div key={l} className="flex flex-col gap-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Description ({l})</Label>
            <Textarea data-testid={`prop-desc-${l}`} rows={4} className={inputCls} value={f.description[l]} onChange={(e) => setLang("description", l, e.target.value)} />
          </div>
        ))}
      </div>

      {/* Features */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {LANGS.map((l) => (
          <div key={l} className="flex flex-col gap-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Features ({l}) — one per line</Label>
            <Textarea data-testid={`prop-features-${l}`} rows={4} className={inputCls} value={f.features[l]} onChange={(e) => setLang("features", l, e.target.value)} />
          </div>
        ))}
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Listing type</Label>
          <Select value={f.listing_type} onValueChange={(v) => set("listing_type", v)}>
            <SelectTrigger data-testid="prop-listing-type" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sale">For Sale</SelectItem>
              <SelectItem value="rental">Short-Term Rental (per night)</SelectItem>
              <SelectItem value="mid_term">Mid-Term Rental (per month, 1–11 months)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{f.listing_type === "mid_term" ? "Price per month" : f.listing_type === "rental" ? "Price per night" : "Price"}</Label>
          <Input data-testid="prop-price" type="number" className={inputCls} value={f.price} onChange={(e) => set("price", e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Currency</Label>
          <Input data-testid="prop-price-unit" className={inputCls} value={f.price_unit} onChange={(e) => set("price_unit", e.target.value)} />
        </div>
      </div>

      {f.listing_type === "mid_term" && (
        <div className="grid grid-cols-2 gap-3 sm:max-w-md" data-testid="prop-stay-range">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Min stay (months)</Label>
            <Input data-testid="prop-min-stay" type="number" min={1} max={11} className={inputCls} value={f.min_stay_months} onChange={(e) => set("min_stay_months", e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Max stay (months)</Label>
            <Input data-testid="prop-max-stay" type="number" min={1} max={11} className={inputCls} value={f.max_stay_months} onChange={(e) => set("max_stay_months", e.target.value)} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="flex flex-col gap-1.5 sm:col-span-1">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Bedrooms</Label>
          <Input data-testid="prop-bedrooms" type="number" className={inputCls} value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Bathrooms</Label>
          <Input data-testid="prop-bathrooms" type="number" className={inputCls} value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Area (m²)</Label>
          <Input data-testid="prop-area" type="number" className={inputCls} value={f.area_sqm} onChange={(e) => set("area_sqm", e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Location</Label>
          <Input data-testid="prop-location" className={inputCls} value={f.location} onChange={(e) => set("location", e.target.value)} />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Image URLs — one per line</Label>
        <Textarea data-testid="prop-images" rows={3} className={inputCls} value={f.images} onChange={(e) => set("images", e.target.value)} />
      </div>

      <div className="flex items-center gap-3">
        <Switch data-testid="prop-featured" checked={f.featured} onCheckedChange={(v) => set("featured", v)} />
        <Label className="text-sm text-foreground/80">Featured on homepage</Label>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button type="submit" disabled={saving} data-testid="prop-save" className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-8 py-3 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
          {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : editing ? "Update Property" : "Create Property"}
        </button>
        <button type="button" onClick={onCancel} data-testid="prop-cancel" className="px-6 py-3 uppercase tracking-[0.2em] text-xs font-body border border-border/60 text-foreground/70 hover:text-foreground transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default PropertyForm;
